import { useTranslation } from "react-i18next";
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
// 引入对应图片（需放入 src/assets/images/）
import equityTopImg from "../assets/images/equity-top.webp";
import focus1Img from "../assets/images/equity-focus1.webp";
import focus2Img from "../assets/images/equity-focus2.webp";
import focus3Img from "../assets/images/equity-focus3.webp";
import fieldImg1 from "../assets/images/lunbo01.png";
import fieldImg1_2 from "../assets/images/lunbo02.png";
import fieldImg2 from "../assets/images/equity-field2.png";
import fieldImg3 from "../assets/images/equity-field3.png";

import field1Img_en from "../assets/images/field1Img_en.png";
import field1Img_zh from "../assets/images/field1Img_zh.png";
import field1Img_zh_TW from "../assets/images/field1Img_zh_TW.png";

import field2Img_en from "../assets/images/field2Img_en.png";
import field2Img_zh from "../assets/images/field2Img_zh.png";
import field2Img_zh_TW from "../assets/images/field2Img_zh_TW.jpg";

import "./EquityInvestmentSection.scss";
import TextPressure from "../repo/TextPressure";
import { useIsMobile } from "../hooks/useIsMobile";
import Carousel from "../components/global/Carousel";

import { getNewsListRq } from "../apis/index";

const EquityInvestmentSection = forwardRef(
  ({ activeFieldIndex: parentActiveIndex }, ref) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;

    // 使用自定义 Hook，可自定义分界值（如 375）
    const isMobile = useIsMobile(768);

    // 1. 定义状态：记录当前激活的 field 索引（-1 表示未激活）
    const [activeFieldIndex, setActiveFieldIndex] = useState(-1);
    const targetDivRef = useRef(null); // 这个 ref 对应组件内部的目标 div

    // 新增：监听父组件传递的 activeFieldIndex，同步更新子组件状态
    useEffect(() => {
      if (parentActiveIndex !== undefined) {
        setActiveFieldIndex(parentActiveIndex);
      }
    }, [parentActiveIndex]);

    // 2. 配置「field 索引」与「图片」的映射关系
    const filedImg1MapLocale = {
      en: field1Img_en,
      zh: field1Img_zh,
      "zh-TW": field1Img_zh_TW,
    };

    // 2. 配置「field 索引」与「图片」的映射关系
    const fieldImg2MapLocale = {
      en: field2Img_en,
      zh: field2Img_zh,
      "zh-TW": field2Img_zh_TW,
    };
    const fieldImgMap = [
      [fieldImg1, filedImg1MapLocale[currentLang]], // 索引 0 对应第一个 field-item 的图片
      [fieldImg2, fieldImg2MapLocale[currentLang]], // 索引 1 对应第二个 field-item 的图片
      [fieldImg3], // 索引 2 对应第三个 field-item 的图片
    ];

    // 3. 计算当前要显示的图片：激活状态显示对应图片，未激活显示默认图片
    const currentFieldImgs =
      activeFieldIndex >= 0
        ? fieldImgMap[activeFieldIndex]
        : [fieldImg1, filedImg1MapLocale[currentLang]];
    useImperativeHandle(
      ref,
      () => ({
        // 暴露修改状态的方法
        setActiveFieldIndex: (index) => setActiveFieldIndex(index),
        // 暴露目标 div 的 ref（用于精准滚动）
        targetDiv: targetDivRef.current,
      }),
      [] // 空依赖：setActiveFieldIndex 是 useState 稳定返回值，targetDivRef 是 useRef 稳定引用
    );
    return (
      <section className='equity-section'>
        {/* 1. 标题区 */}
        <div className='equity-header g-inner ' id={t("navbar.investment")}>
          {currentLang === "en" ? (
            <div style={{ position: "relative", height: "100px" }}>
              <TextPressure
                text={t("equity.title")}
                className='equity-title'
                flex={true}
                alpha={false}
                stroke={false}
                scale={false}
                width={true}
                weight={true}
                italic={true}
                textColor='#000000'
                strokeColor='#000000'
                minFontSize={16}
              />
            </div>
          ) : (
            <div className='equity-title-zh'>{t("equity.title")}</div>
          )}
          <p className='equity-subtitle'>{t("equity.subtitle")}</p>
        </div>

        {/* 2. 描述+右侧图片区 */}
        <div className='equity-desc-img'>
          <div className='container g-inner'>
            <div className='desc-text sub-font'>
              <p>{t("equity.desc")}</p>
            </div>
            <div className='desc-img'>
              <img src={equityTopImg} alt={t("equity.topImgAlt")} />
            </div>
          </div>
        </div>

        {/* 3. 我们专注三件事 - 卡片区 */}
        <div className='equity-focus'>
          <div className='container g-inner'>
            <h3 className='focus-title sub-font'>{t("equity.focusTitle")}</h3>
            <div className='focus-cards'>
              <div className='focus-card'>
                <div className='card-title main-font'>
                  {t("equity.focus1Title")}

                  {/* 条件渲染移动端节点 */}
                  {isMobile && (
                    <p className='card-title-mobile'>
                      {t("equity.focus1Content")}
                    </p>
                  )}
                </div>

                <div className='card-content'>
                  <img
                    src={focus1Img}
                    alt={t("equity.focus2Alt")}
                    className='card-img'
                  />
                  <div className='card-content-bottom'>
                    {t("equity.focus1Content")}
                  </div>
                </div>
              </div>
              <div className='focus-card'>
                <div className='card-title main-font'>
                  {t("equity.focus2Title")}

                  {/* 条件渲染移动端节点 */}
                  {isMobile && (
                    <p className='card-title-mobile'>
                      {t("equity.focus2Content")}
                    </p>
                  )}
                </div>
                <div className='card-content'>
                  <img
                    src={focus2Img}
                    alt={t("equity.focus2Alt")}
                    className='card-img'
                  />
                  <div className='card-content-bottom'>
                    {t("equity.focus2Content")}
                  </div>
                </div>
              </div>
              <div className='focus-card'>
                <div className='card-title main-font'>
                  {t("equity.focus3Title")}

                  {/* 条件渲染移动端节点 */}
                  {isMobile && (
                    <p className='card-title-mobile'>
                      {t("equity.focus3Content")}
                    </p>
                  )}
                </div>
                <div className='card-content'>
                  <img
                    src={focus3Img}
                    alt={t("equity.focus3Alt")}
                    className='card-img'
                  />
                  <div className='card-content-bottom'>
                    {t("equity.focus3Content")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. 投资策略区 */}
        <div className='equity-fields' ref={targetDivRef}>
          <div className='container g-inner'>
            <h3 className='fields-title sub-font'>{t("equity.fieldTitle")}</h3>
            <div className='fields-wrap'>
              <div className='fields-list'>
                {/* 第一个 field-item：鼠标移入/移出更新索引 */}
                <div
                  className={`tech-card field-item ${
                    activeFieldIndex === 0 ? "active" : ""
                  }`}
                  onClick={() => setActiveFieldIndex(-1)}
                  // 可选：给激活的项目添加样式
                  style={{
                    cursor: "pointer",
                    opacity: activeFieldIndex === 0 ? 0.9 : 1,
                  }}
                >
                  <p className='tech-title'>{t("equity.field1")}</p>
                  <p className='tech-desc sub-font'>
                    {t("equity.field1Content")}
                  </p>
                </div>

                {/* 第二个 field-item */}
                <div
                  className={`tech-card field-item ${
                    activeFieldIndex === 1 ? "active" : ""
                  }`}
                  onClick={() => setActiveFieldIndex(1)}
                  style={{
                    cursor: "pointer",
                    opacity: activeFieldIndex === 1 ? 0.9 : 1,
                  }}
                >
                  <p className='tech-title'>{t("equity.field2")}</p>
                  <p className='tech-desc sub-font'>
                    {t("equity.field2Content")}
                  </p>
                </div>

                {/* 第三个 field-item */}
                <div
                  className={`tech-card field-item ${
                    activeFieldIndex === 2 ? "active" : ""
                  }`}
                  onClick={() => setActiveFieldIndex(2)}
                  style={{
                    cursor: "pointer",
                    opacity: activeFieldIndex === 2 ? 0.9 : 1,
                  }}
                >
                  <p className='tech-title'>{t("equity.field3")}</p>
                  <p className='tech-desc sub-font'>
                    {t("equity.field3Content")}
                  </p>
                </div>
              </div>
              <div className='fields-img' style={{ position: "relative" }}>
                <Carousel images={currentFieldImgs} />

                {/* 显示当前匹配的图片 */}
                {/* <img
                src={currentFieldImg}
                alt={t("equity.fieldImgAlt")}
                // 可选：添加图片切换过渡动画
                style={{ transition: "opacity 0.3s ease", opacity: 1 }}
              /> */}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default EquityInvestmentSection;
